import { 
  doc, 
  getDoc, 
  writeBatch,
  collection,
  addDoc
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { rushdApiService } from './rushdService';

// Discount Code Interface
export interface DiscountCode {
  id: string;
  code: string;
  description: string;
  type: 'percentage' | 'fixed' | 'free_shipping';
  value: number; // percentage (0-100) or fixed amount
  minOrderAmount: number;
  maxUses: number;
  currentUses: number;
  status: 'active' | 'inactive' | 'expired';
  startDate: Date;
  endDate: Date;
  applicableProducts: string[]; // product categories or 'all'
  affiliateId?: string; // Link to affiliate for commission tracking
  membersOnly: boolean; // Rush'd members-only discount flag
  createdAt: Date;
  updatedAt: Date;
}

// Discount Config Interface
export interface DiscountConfig {
  discountCodes: DiscountCode[];
  lastUpdated?: Date;
}

// Discount Usage Interface
export interface DiscountUsage {
  id: string;
  discountCodeId: string;
  discountCode: string;
  orderId: string;
  userId: string;
  userEmail: string;
  discountAmount: number;
  orderTotal: number;
  usedAt: Date;
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Get discount configuration from Firestore
 */
export const getDiscountConfig = async (): Promise<DiscountConfig> => {
  try {
    const discountRef = doc(db, 'config', 'discounts');
    const discountDoc = await getDoc(discountRef);
    
    if (!discountDoc.exists()) {
      return { discountCodes: [] };
    }
    
    const data = discountDoc.data();
    const discountCodes: DiscountCode[] = [];
    
    if (data.discountCodes && Array.isArray(data.discountCodes)) {
      data.discountCodes.forEach((discount: any) => {
        discountCodes.push({
          id: discount.id || `discount-${Date.now()}`,
          code: discount.code || '',
          description: discount.description || '',
          type: discount.type || 'percentage',
          value: discount.value || 0,
          minOrderAmount: discount.minOrderAmount || 0,
          maxUses: discount.maxUses || 100,
          currentUses: discount.currentUses || 0,
          status: discount.status || 'active',
          startDate: discount.startDate?.toDate ? discount.startDate.toDate() : new Date(),
          endDate: discount.endDate?.toDate ? discount.endDate.toDate() : new Date(),
          applicableProducts: discount.applicableProducts || ['all'],
          affiliateId: discount.affiliateId || undefined,
          membersOnly: !!discount.membersOnly,
          createdAt: discount.createdAt?.toDate ? discount.createdAt.toDate() : new Date(),
          updatedAt: discount.updatedAt?.toDate ? discount.updatedAt.toDate() : new Date()
        });
      });
    }
    
    return {
      discountCodes: discountCodes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
      lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date()
    };
  } catch (error) {
    console.error('Error fetching discount config:', error);
    return { discountCodes: [] };
  }
};

/**
 * Get user-specific usage count for a discount code
 */
export const getUserUsageCount = async (discountCodeId: string, userId: string): Promise<number> => {
  try {
    // For now, we'll implement a simple check
    // In a production app, you might want to query the discount_usage collection
    return 0; // Placeholder - implement based on your needs
  } catch (error) {
    console.error('Error fetching user usage count:', error);
    return 0;
  }
};

/**
 * Validate discount code for cart preview (without authentication)
 * @param code - The discount code to validate
 * @param orderSubtotal - The cart subtotal (before shipping and fees) to validate against
 * @param currencyOptions - Optional currency conversion options for error display
 */
export const validateDiscountCodeForCart = async (
  code: string, 
  orderSubtotal: number,
  currencyOptions?: {
    getConvertedItemPrice: (usdPrice: number) => number;
    formatDisplayAmount: (amount: number) => string;
    selectedCurrency: string;
  }
): Promise<{
  isValid: boolean;
  discount?: DiscountCode;
  discountAmount?: number;
  error?: string;
}> => {
  try {
    // Input validation
    if (!code || !code.trim()) {
      return { isValid: false, error: 'Please enter a discount code' };
    }

    if (orderSubtotal <= 0) {
      return { isValid: false, error: 'Cart subtotal must be greater than $0' };
    }

    const config = await getDiscountConfig();
    const discount = config.discountCodes.find(d => 
      d.code.toUpperCase() === code.trim().toUpperCase() && 
      d.status === 'active'
    );
    
    if (!discount) {
      return { isValid: false, error: 'Invalid discount code' };
    }

    if (discount.membersOnly) {
      return { 
        isValid: false, 
        error: "You need to be a JamPack'd Rush'd member to use this discount code." 
      };
    }
    
    // Check date validity with timezone-safe comparison
    const now = new Date();
    
    // Create date objects for comparison that account for the full day
    // Start date should be valid from the beginning of the day
    const startOfStartDate = new Date(discount.startDate);
    startOfStartDate.setHours(0, 0, 0, 0);
    
    // End date should be valid until the end of the day
    const endOfEndDate = new Date(discount.endDate);
    endOfEndDate.setHours(23, 59, 59, 999);
    
    if (now < startOfStartDate || now > endOfEndDate) {
      return { isValid: false, error: 'Discount code has expired' };
    }
    
    // Check minimum order amount (based on subtotal)
    if (orderSubtotal < discount.minOrderAmount) {
      // Use currency conversion if available, otherwise default to USD
      let displayAmount: string;
      if (currencyOptions) {
        const convertedMinAmount = currencyOptions.getConvertedItemPrice(discount.minOrderAmount);
        displayAmount = currencyOptions.formatDisplayAmount(convertedMinAmount);
      } else {
        displayAmount = `$${discount.minOrderAmount.toFixed(2)}`;
      }
      
      return { 
        isValid: false, 
        error: `Cart subtotal must be at least ${displayAmount}` 
      };
    }
    
    // Check usage limits (but not user-specific limits since we don't have auth)
    if (discount.currentUses >= discount.maxUses) {
      return { isValid: false, error: 'Discount code usage limit reached' };
    }
    
    // Calculate discount amount based on cart subtotal
    let discountAmount = 0;
    switch (discount.type) {
      case 'percentage':
        // Apply percentage discount to cart subtotal
        discountAmount = (orderSubtotal * discount.value) / 100;
        break;
      case 'fixed':
        // For fixed discounts, never exceed the cart subtotal
        discountAmount = Math.min(discount.value, orderSubtotal);
        break;
      case 'free_shipping':
        // Free shipping discounts don't reduce cart subtotal
        // The shipping cost reduction is handled in the cart logic
        discountAmount = 0;
        break;
    }
    
    // Ensure discount amount is not negative and is properly rounded
    discountAmount = Math.max(0, Math.round(discountAmount * 100) / 100);
    
    return {
      isValid: true,
      discount,
      discountAmount
    };
  } catch (error) {
    console.error('Error validating discount code:', error);
    return { isValid: false, error: 'Failed to validate discount code' };
  }
};

/**
 * Validate discount code for checkout (with authentication)
 * @param code - The discount code to validate
 * @param userId - The user ID applying the discount
 * @param orderSubtotal - The cart subtotal (before shipping and fees) to validate against
 * @param userEmail - The user's email address
 * @param currencyOptions - Optional currency conversion options for error display
 */
export const validateDiscountCode = async (
  code: string, 
  userId: string, 
  orderSubtotal: number,
  userEmail: string,
  currencyOptions?: {
    getConvertedItemPrice: (usdPrice: number) => number;
    formatDisplayAmount: (amount: number) => string;
    selectedCurrency: string;
  }
): Promise<{
  isValid: boolean;
  discount?: DiscountCode;
  discountAmount?: number;
  error?: string;
}> => {
  try {
    // Input validation
    if (!code || !code.trim()) {
      return { isValid: false, error: 'Please enter a discount code' };
    }

    if (orderSubtotal <= 0) {
      return { isValid: false, error: 'Cart subtotal must be greater than $0' };
    }

    const config = await getDiscountConfig();
    const discount = config.discountCodes.find(d => 
      d.code.toUpperCase() === code.trim().toUpperCase() && 
      d.status === 'active'
    );
    
    if (!discount) {
      return { isValid: false, error: 'Invalid discount code' };
    }
    
    // Check Rush'd members-only requirement
    if (discount.membersOnly) {
      try {
        const membershipStatus = await rushdApiService.getMembershipStatusFromFirebase(userId);
        if (!membershipStatus.isMember || !membershipStatus.isActive) {
          return { 
            isValid: false, 
            error: "You need to be a JamPack'd Rush'd member to use this discount code." 
          };
        }
      } catch (error) {
        console.error('Error checking Rush\'d membership for discount validation:', error);
        // If we can't verify membership status, deny access to members-only codes
        return { 
          isValid: false, 
          error: 'Unable to verify membership status. Please try again.' 
        };
      }
    }
    
    // Check date validity with timezone-safe comparison
    const now = new Date();
    
    // Create date objects for comparison that account for the full day
    // Start date should be valid from the beginning of the day
    const startOfStartDate = new Date(discount.startDate);
    startOfStartDate.setHours(0, 0, 0, 0);
    
    // End date should be valid until the end of the day
    const endOfEndDate = new Date(discount.endDate);
    endOfEndDate.setHours(23, 59, 59, 999);
    
    if (now < startOfStartDate || now > endOfEndDate) {
      return { isValid: false, error: 'Discount code has expired' };
    }
    
    // Check minimum order amount (based on subtotal)
    if (orderSubtotal < discount.minOrderAmount) {
      // Use currency conversion if available, otherwise default to USD
      // Use the same conversion logic as shipping costs: getConvertedItemPrice()
      let displayAmount: string;
      if (currencyOptions) {
        const convertedMinAmount = currencyOptions.getConvertedItemPrice(discount.minOrderAmount);
        displayAmount = currencyOptions.formatDisplayAmount(convertedMinAmount);
      } else {
        displayAmount = `$${discount.minOrderAmount.toFixed(2)}`;
      }
      
      return { 
        isValid: false, 
        error: `Cart subtotal must be at least ${displayAmount}` 
      };
    }
    
    // Check usage limits
    if (discount.currentUses >= discount.maxUses) {
      return { isValid: false, error: 'Discount code usage limit reached' };
    }
    
    // Check user-specific usage (optional: implement per-user limits)
    const userUsageCount = await getUserUsageCount(discount.id, userId);
    const maxUsesPerUser = 1; // You can make this configurable per discount
    if (userUsageCount >= maxUsesPerUser) {
      return { isValid: false, error: 'You have already used this discount code' };
    }
    
    // Calculate discount amount based on cart subtotal
    let discountAmount = 0;
    switch (discount.type) {
      case 'percentage':
        // Apply percentage discount to cart subtotal
        discountAmount = (orderSubtotal * discount.value) / 100;
        break;
      case 'fixed':
        // For fixed discounts, never exceed the cart subtotal
        discountAmount = Math.min(discount.value, orderSubtotal);
        break;
      case 'free_shipping':
        // Free shipping discounts don't reduce cart subtotal
        // The shipping cost reduction is handled in the cart logic
        discountAmount = 0;
        break;
    }
    
    // Ensure discount amount is not negative and is properly rounded
    discountAmount = Math.max(0, Math.round(discountAmount * 100) / 100);
    
    return {
      isValid: true,
      discount,
      discountAmount
    };
  } catch (error) {
    console.error('Error validating discount code:', error);
    return { isValid: false, error: 'Failed to validate discount code' };
  }
};

/**
 * Apply discount code atomically during order creation
 */
export const applyDiscountCodeAtomic = async (
  discountCode: string,
  orderId: string,
  userId: string,
  userEmail: string,
  orderTotal: number,
  discountAmount: number
): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    // Get current discount config
    const config = await getDiscountConfig();
    const discount = config.discountCodes.find(d => 
      d.code.toUpperCase() === discountCode.toUpperCase()
    );
    
    if (!discount) {
      throw new Error('Discount code not found');
    }
    
    // Update discount usage count
    const updatedDiscountCodes = config.discountCodes.map(d => 
      d.id === discount.id 
        ? { ...d, currentUses: d.currentUses + 1, updatedAt: new Date() }
        : d
    );
    
    const discountRef = doc(db, 'config', 'discounts');
    batch.set(discountRef, {
      discountCodes: updatedDiscountCodes.map(d => {
        const discountData: any = {
          ...d,
          startDate: d.startDate,
          endDate: d.endDate,
          createdAt: d.createdAt,
          updatedAt: d.updatedAt
        };
        
        // Only include affiliateId if it has a value
        if (d.affiliateId) {
          discountData.affiliateId = d.affiliateId;
        }
        
        return discountData;
      }),
      lastUpdated: new Date()
    }, { merge: true });
    
    // Log usage in separate collection
    const usageRef = collection(db, 'discount_usage');
    const usageDoc = doc(usageRef);
    const usageData: any = {
      discountCodeId: discount.id,
      discountCode: discount.code,
      orderId,
      userId,
      userEmail,
      discountAmount,
      orderTotal,
      usedAt: new Date(),
      ipAddress: '', // This would be passed from the frontend
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
    };
    
    // Only include affiliateId if it has a value
    if (discount.affiliateId) {
      usageData.affiliateId = discount.affiliateId;
    }
    
    batch.set(usageDoc, usageData);
    
    // Commit the batch
    await batch.commit();
    
  } catch (error) {
    console.error('Error applying discount code:', error);
    throw new Error('Failed to apply discount code');
  }
};

/**
 * Get discount code by code string
 */
export const getDiscountByCode = async (code: string): Promise<DiscountCode | null> => {
  try {
    const config = await getDiscountConfig();
    const discount = config.discountCodes.find(d => 
      d.code.toUpperCase() === code.toUpperCase() && 
      d.status === 'active'
    );
    
    return discount || null;
  } catch (error) {
    console.error('Error fetching discount by code:', error);
    return null;
  }
};

/**
 * Check if discount code is available for use
 */
export const isDiscountCodeAvailable = async (code: string): Promise<boolean> => {
  try {
    const discount = await getDiscountByCode(code);
    if (!discount) return false;
    
    const now = new Date();
    
    // Create date objects for comparison that account for the full day
    // Start date should be valid from the beginning of the day
    const startOfStartDate = new Date(discount.startDate);
    startOfStartDate.setHours(0, 0, 0, 0);
    
    // End date should be valid until the end of the day
    const endOfEndDate = new Date(discount.endDate);
    endOfEndDate.setHours(23, 59, 59, 999);
    
    return (
      discount.status === 'active' &&
      now >= startOfStartDate &&
      now <= endOfEndDate &&
      discount.currentUses < discount.maxUses
    );
  } catch (error) {
    console.error('Error checking discount availability:', error);
    return false;
  }
}; 
